// src/admin/screens/login/TotpChallenge.tsx
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@admin/lib/supabase';
import { useAuth } from '@admin/auth/useAuth';
import styles from './Login.module.css';

const schema = z.object({ code: z.string().regex(/^\d{6}$/, '6 digits') });
type FormValues = z.infer<typeof schema>;

export default function TotpChallenge() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/';
  const { refresh } = useAuth();
  const [factorId, setFactorId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { register, handleSubmit, formState: { isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    void (async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      const verified = data?.totp.find((f) => f.status === 'verified');
      if (!verified) { navigate('/login/enrol-totp', { replace: true }); return; }
      setFactorId(verified.id);
    })();
  }, [navigate]);

  const onSubmit = async ({ code }: FormValues) => {
    if (!factorId) return;
    setErr(null);
    const ch = await supabase.auth.mfa.challenge({ factorId });
    if (ch.error) { setErr(ch.error.message); return; }
    const v = await supabase.auth.mfa.verify({ factorId, challengeId: ch.data.id, code });
    if (v.error) { setErr(v.error.message); return; }
    await refresh();
    navigate(next, { replace: true });
  };

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={handleSubmit(onSubmit)} noValidate>
        <h1>Two-step verification</h1>
        <p className="small">Enter the 6-digit code from your authenticator.</p>
        <label>
          Code
          <input inputMode="numeric" autoFocus maxLength={6} {...register('code')} />
        </label>
        {err && <div className={styles.error}>{err}</div>}
        <button type="submit" className={styles.submit} disabled={isSubmitting || !factorId}>
          {isSubmitting ? 'Verifying…' : 'Verify'}
        </button>
      </form>
    </div>
  );
}
